/**
 * Reporte de IGTF percibido para imprimir o guardar como PDF (pedido del 2026-10-11): por cobro en divisas, con la
 * base, el IVA y el total de la factura en Bs, lo pagado en divisas en Bs y el IGTF en Bs; las facturas manuales con su
 * IGTF escrito a mano y el subtotal por quincena (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H1). Carta apaisada.
 * Imprimir → "Guardar como PDF". Los números son los mismos de la pantalla y del Excel.
 */
import { formatearTasa, Money, type ReporteIgtfDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { get, mensajeDeError } from '@/lib/api.ts';

const fechaCorta = (f: string) => `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)}`;
const bs = (v: string | null) => (v === null ? '' : Money.parse(v).toDisplay());

export function ImprimirReporteIgtf() {
  const [params] = useSearchParams();
  const desde = params.get('desde') ?? '';
  const hasta = params.get('hasta') ?? '';
  const sucursalId = params.get('sucursal_id');
  const q = { desde, hasta, ...(sucursalId ? { sucursal_id: sucursalId } : {}) };
  const r = useQuery({ queryKey: ['reportes', 'igtf', q], queryFn: () => get<ReporteIgtfDTO>('/reportes/igtf', q), enabled: !!desde && !!hasta });
  useEffect(() => {
    if (r.data) { const t = setTimeout(() => window.print(), 300); return () => clearTimeout(t); }
    return undefined;
  }, [r.data]);
  if (r.error) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>No se pudo preparar el reporte: {mensajeDeError(r.error)}</p>;
  if (!r.data) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>Preparando reporte…</p>;
  const d = r.data;
  const th = { borderBottom: '1px solid #000', padding: '2px 4px' } as const;
  const td = { padding: '2px 4px', borderBottom: '1px solid #ddd' } as const;
  return (
    <div style={{ fontFamily: 'Georgia, serif', fontSize: 10, width: '10.2in', margin: '0 auto', padding: 8 }} data-testid="formato-igtf">
      <style>{`@page { size: letter landscape; margin: 10mm } .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap } thead { display: table-header-group } tr { page-break-inside: avoid } @media screen { body { background: #e5e7eb } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: 0 }}>IGTF percibido · {d.sucursal_nombre ?? 'Todas las sucursales'}</h2>
        <button type="button" className="no-print" onClick={() => window.print()} style={{ padding: '4px 12px', cursor: 'pointer' }} data-testid="formato-igtf-imprimir">Imprimir o guardar en PDF</button>
      </div>
      {/* Cobros y facturas manuales por separado (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H1): antes «N cobro(s)» contaba también las manuales */}
      <p style={{ margin: '2px 0 8px' }} data-testid="formato-igtf-encabezado">
        Del {fechaCorta(d.desde)} al {fechaCorta(d.hasta)} · {d.cobros} cobro(s) con pago en divisas · {d.facturas.documentos} factura(s){d.manuales.length > 0 ? ` · ${d.manuales.length} factura(s) manual(es) con IGTF escrito a mano` : ''}. Montos en bolívares; lo pagado en divisas y su IGTF, a la tasa BCV del día de cada cobro.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Fecha</th>
            <th style={{ ...th, textAlign: 'left' }}>Documento</th>
            <th style={{ ...th, textAlign: 'left' }}>N.º de control</th>
            <th style={{ ...th, textAlign: 'left' }}>RIF</th>
            <th style={{ ...th, textAlign: 'left' }}>Cliente</th>
            <th className="num" style={th}>Base Bs</th>
            <th className="num" style={th}>Exento Bs</th>
            <th className="num" style={th}>IVA Bs</th>
            <th className="num" style={th}>Total Bs</th>
            <th className="num" style={th}>Pagado en divisas $</th>
            <th className="num" style={th}>Tasa BCV</th>
            <th className="num" style={th}>Pagado en divisas Bs</th>
            <th className="num" style={th}>IGTF Bs</th>
          </tr>
        </thead>
        <tbody>
          {d.pagos.map((p) => (
            <tr key={p.pago_id} data-testid={`formato-igtf-${p.documento_numero}`}>
              <td style={td}>{fechaCorta(p.fecha)}</td>
              <td style={td}>{p.documento_tipo === 'ABONO' ? 'Anticipo' : p.documento_tipo} {p.documento_numero}</td>
              <td style={td}>{p.control ?? ''}</td>
              <td style={td}>{p.cliente_rif}</td>
              <td style={td}>{p.cliente_razon_social}</td>
              <td className="num" style={td}>{bs(p.factura_base_bs)}</td>
              <td className="num" style={td}>{bs(p.factura_exento_bs)}</td>
              <td className="num" style={td}>{bs(p.factura_iva_bs)}</td>
              <td className="num" style={td}>{bs(p.factura_total_bs)}</td>
              <td className="num" style={td}>{Money.parse(p.base_divisa).toDisplay()}</td>
              <td className="num" style={td}>{p.tasa_bcv_cobro ? formatearTasa(p.tasa_bcv_cobro) : ''}</td>
              <td className="num" style={td}>{bs(p.pagado_divisas_bs)}</td>
              <td className="num" style={td}>{bs(p.igtf_bs)}</td>
            </tr>
          ))}
          {/* Facturas de talonario manual: su IGTF escrito a mano ya está en el total de abajo, así la columna lo suma (hallazgo 6-H1, ronda 2) */}
          {d.manuales.map((m) => (
            <tr key={m.id} data-testid={`formato-igtf-manual-${m.numero}`}>
              <td style={td}>{fechaCorta(m.fecha)}</td>
              <td style={td}>FAC manual {m.numero}</td>
              <td style={td}>{m.control ?? ''}</td>
              <td style={td}>{m.cliente_rif}</td>
              <td style={td}>{m.cliente_razon_social}</td>
              <td style={td} colSpan={7}>IGTF escrito a mano en la factura (solo en Bs)</td>
              <td className="num" style={td}>{bs(m.igtf_bs)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 'bold' }} data-testid="formato-igtf-totales">
            <td style={{ ...td, borderTop: '1px solid #000' }} colSpan={5}>TOTALES (cada factura contada una vez)</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.facturas.base_bs)}</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.facturas.exento_bs)}</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.facturas.iva_bs)}</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.facturas.total_bs)}</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{Money.parse(d.total_base_divisa).toDisplay()}</td>
            <td style={{ ...td, borderTop: '1px solid #000' }} />
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.total_pagado_divisas_bs)}</td>
            <td className="num" style={{ ...td, borderTop: '1px solid #000' }}>{bs(d.total_igtf_bs)}</td>
          </tr>
        </tbody>
      </table>
      {d.pagos.length === 0 && d.manuales.length === 0 ? <p style={{ marginTop: 8 }}>Sin cobros en divisas en el período.</p> : null}
      {/* Subtotal por quincena, como en la pantalla y el Excel: el IGTF se declara por quincena (hallazgo 6-H1, ronda 2) */}
      {d.por_quincena.length > 0 ? (
        <table style={{ width: '60%', borderCollapse: 'collapse', marginTop: 10 }} data-testid="formato-igtf-quincenas">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Mes</th>
              <th style={{ ...th, textAlign: 'left' }}>Quincena</th>
              <th className="num" style={th}>Operaciones</th>
              <th className="num" style={th}>Pagado en divisas Bs</th>
              <th className="num" style={th}>IGTF Bs</th>
            </tr>
          </thead>
          <tbody>
            {d.por_quincena.map((x) => (
              <tr key={`${x.mes}-${x.quincena}`} data-testid={`formato-igtf-quincena-${x.mes}-${x.quincena}`}>
                <td style={td}>{x.mes.slice(5, 7)}/{x.mes.slice(0, 4)}</td>
                <td style={td}>{x.quincena === '1ra' ? 'Del 1 al 15' : 'Del 16 al fin de mes'}</td>
                <td className="num" style={td}>{x.operaciones}</td>
                <td className="num" style={td}>{bs(x.pagado_divisas_bs)}</td>
                <td className="num" style={td}>{bs(x.igtf_bs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <p style={{ marginTop: 8, fontSize: 9 }}>
        Los anticipos no tienen factura: su base, IVA y total van en blanco. Una factura con varios cobros en divisas aparece una vez por cobro; en los totales cuenta una sola vez. Las facturas manuales traen solo el IGTF escrito a mano, en Bs.
      </p>
    </div>
  );
}
